import { first } from 'lodash';

import { AugmentedUpdateClientPresentationV2Input } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { formatDateToMMDDYY } from '@/utils/formatting/dates';
import { getNodes } from '@/utils/graphqlUtils';

import {
  mapBundleToForm,
  mapBundleToUpdateInput,
  NEW_BUNDLE_ID_SENTINEL,
  NEW_PAGE_ID_SENTINEL,
  remapBundle,
  remapPage,
} from '../ClientPresentationDesigner.utils';
import { isSinglePageBundle } from '../components/ClientPresentationDesignerTreeView/ClientPresentationDesignerTreeView.constants';
import {
  BuildSaveInputArgs,
  PresentationSaveStrategy,
  ReconcileSaveArgs,
  ReconcileSaveResult,
  ResolveContextMapKeyArgs,
} from './presentationSave.types';
import { preserveCurrentEstateInsights } from './reconcilePresentation';

/**
 * Builds the full-write input: `clearBundles` drops every bundle, then
 * `withBundles` recreates all of them from form state.
 */
function buildPresentationInput({
  bundles,
  presentationConfiguration,
  title,
  context,
}: BuildSaveInputArgs): AugmentedUpdateClientPresentationV2Input {
  return {
    id: context.presentationId,
    update: {
      name: title ?? `Untitled Presentation ${formatDateToMMDDYY(new Date())}`,
      pageNumberToStartFrom: presentationConfiguration.pageNumberToStartFrom,
      showLegalDisclaimer: presentationConfiguration.showLegalDisclaimer,
      showClientNamesInHeader:
        presentationConfiguration.showClientNamesInHeader,
      showPageNumbers: presentationConfiguration.showPageNumbers,
      showPageNumbersOnCoverSlide:
        presentationConfiguration.showPageNumbersOnCoverSlide,
      clearBundles: true,
    },
    withBundles: bundles.map((bundle, index) =>
      mapBundleToUpdateInput(bundle, context.householdId, index)
    ),
  };
}

function reconcilePresentation({
  bundles,
  selectedItemId,
  responseData,
  context,
}: ReconcileSaveArgs): ReconcileSaveResult {
  const { householdId, presentationId } = context;
  const responseBundles = getNodes(
    first(getNodes(responseData.clientPresentationV2s))?.bundles
  );
  if (responseBundles.length !== bundles.length) {
    const reason = `response bundle count (${responseBundles.length}) does not match form bundle count (${bundles.length})`;
    diagnostics.warn(`syncBundlesFromResponse: ${reason}`);
    return { kind: 'desynced', reason };
  }

  let needsIDSync = false;
  const updatedBundles = bundles.map((formBundle, index) => {
    const responseBundle = responseBundles[index];
    if (!responseBundle || responseBundle.kind !== formBundle.type) {
      return formBundle;
    }

    if (formBundle.id.startsWith(NEW_BUNDLE_ID_SENTINEL)) {
      const mapped = mapBundleToForm(
        responseBundle,
        householdId,
        presentationId
      );
      if (!mapped) return formBundle;
      needsIDSync = true;
      const reconciledBundle =
        formBundle.treeLabelSuffix === undefined
          ? mapped
          : { ...mapped, treeLabelSuffix: formBundle.treeLabelSuffix };
      return preserveCurrentEstateInsights(formBundle, reconciledBundle);
    }

    const mapped = mapBundleToForm(responseBundle, householdId, presentationId);
    if (!mapped) return formBundle;

    const hasNewPages = formBundle.pages.some((page) =>
      page.id.startsWith(NEW_PAGE_ID_SENTINEL)
    );
    if (hasNewPages) {
      needsIDSync = true;
      return {
        ...formBundle,
        pages: formBundle.pages.map((formPage, pageIndex) =>
          formPage.id.startsWith(NEW_PAGE_ID_SENTINEL)
            ? (mapped.pages[pageIndex] ?? formPage)
            : formPage
        ),
      };
    }

    const configIdChanged = mapped.pages.some((responsePage, pageIndex) => {
      const formPage = formBundle.pages[pageIndex];
      return (
        formPage?.balanceSheetBundleConfiguration &&
        responsePage.balanceSheetBundleConfiguration?.id &&
        formPage.balanceSheetBundleConfiguration.id !==
          responsePage.balanceSheetBundleConfiguration.id
      );
    });

    if (configIdChanged) {
      needsIDSync = true;
      return {
        ...formBundle,
        pages: formBundle.pages.map((formPage, pageIndex) => {
          const responsePage = mapped.pages[pageIndex];
          if (
            formPage.balanceSheetBundleConfiguration &&
            responsePage?.balanceSheetBundleConfiguration?.id
          ) {
            return {
              ...formPage,
              balanceSheetBundleConfiguration: {
                ...formPage.balanceSheetBundleConfiguration,
                id: responsePage.balanceSheetBundleConfiguration.id,
              },
            };
          }
          return formPage;
        }),
      };
    }

    return formBundle;
  });

  if (!needsIDSync) return { kind: 'nothingToSync' };

  let newSelectedId: string | undefined;
  const remappedBundle = remapBundle(selectedItemId, bundles, updatedBundles);
  if (remappedBundle) {
    newSelectedId = isSinglePageBundle(remappedBundle.type)
      ? remappedBundle.id
      : (first(remappedBundle.pages)?.id ?? remappedBundle.id);
  } else {
    const remappedPage = remapPage(selectedItemId, bundles, updatedBundles);
    if (remappedPage && remappedPage.id !== selectedItemId) {
      newSelectedId = remappedPage.id;
    }
  }

  return { kind: 'synced', updatedBundles, newSelectedId };
}

/**
 * Correlates a response bundle to its form bundle by position. `clearBundles`
 * mints new server IDs on every save, so the response IDs never match the form
 * IDs and position is the only correlation available.
 */
function resolveContextMapKey({
  responseBundle,
  index,
  bundles,
}: ResolveContextMapKeyArgs): string | null {
  const formBundleId = bundles[index]?.id;
  if (!formBundleId) {
    diagnostics.warn(
      `Failed to find form bundle ID for bundle ${responseBundle.id} at index ${index}`
    );
    return null;
  }

  // Use the response bundle's ID as the map key.
  // reconcilePresentation updates the form to match server IDs after
  // every persist (clearBundles recreates everything with new IDs).
  return formBundleId.startsWith(NEW_BUNDLE_ID_SENTINEL)
    ? responseBundle.id
    : formBundleId;
}

/**
 * The save that ships today: every write rewrites the whole presentation,
 * clearing every bundle and recreating all of them.
 *
 * This is selected only when `presentation_stable_save` is off. An incremental
 * save never falls back to this strategy. See T1-6071.
 */
export function presentationFullWriteStrategy(): PresentationSaveStrategy {
  return {
    type: 'full',
    buildPresentationInput,
    reconcilePresentation,
    resolveContextMapKey,
  };
}
